// client/src/components/AdminLayout.js
// Layout wrapper for admin mode pages

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminSidebar from './AdminSidebar';
import AdminHeader from './AdminHeader';
import { fetchAccessibleOrganizations } from '../utils/orgDataUtils';

const AdminLayout = ({ children, activeSection, onSectionChange, currentOrgId, onOrgChange, onModeSwitch, onLogOut }) => {
  const { userRole, userOrganizations } = useAuth();
  const [adminOrganizations, setAdminOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Stable key so a new array with the same IDs does not refetch.
  const orgIdsKey = Array.isArray(userOrganizations) ? userOrganizations.join(',') : '';

  // Load organizations once per role / org-membership change. Do not depend on
  // currentOrgId or onOrgChange — those change on every tab click (parent
  // re-render) and used to setLoading(true), which unmounted the sidebar.
  useEffect(() => {
    if (userRole === null) {
      return undefined;
    }

    let cancelled = false;

    const loadAdminOrganizations = async () => {
      try {
        const orgs = await fetchAccessibleOrganizations(userRole, userOrganizations);
        if (!cancelled) {
          setAdminOrganizations(orgs);
        }
      } catch (error) {
        console.error('Error loading admin organizations:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAdminOrganizations();
    return () => {
      cancelled = true;
    };
    // userOrganizations is represented by orgIdsKey to avoid identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, orgIdsKey]);

  // Pick a default org after the list loads, without refetching or hiding the shell.
  useEffect(() => {
    if (!currentOrgId && adminOrganizations.length > 0) {
      onOrgChange(adminOrganizations[0].id);
    }
  }, [currentOrgId, adminOrganizations, onOrgChange]);

  // Full-page spinner only on the first load. Later refetches keep the sidebar
  // mounted so tab changes do not flash the chrome away.
  if (loading && adminOrganizations.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin interface...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen" style={{ backgroundColor: '#f0f1f2' }}>
      <a
        href="#admin-main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-blue-700 focus:rounded-md focus:ring-2 focus:ring-blue-500"
      >
        Skip to main content
      </a>
      {/* Sidebar */}
      <AdminSidebar
        activeSection={activeSection}
        onSectionChange={onSectionChange}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <AdminHeader
          organizations={adminOrganizations}
          currentOrgId={currentOrgId}
          onOrgChange={onOrgChange}
          activeSection={activeSection}
          onModeSwitch={onModeSwitch}
          onLogOut={onLogOut}
        />

        {/* Content */}
        <main id="admin-main-content" className="flex-1 overflow-auto" style={{ backgroundColor: '#f0f1f2' }}>
          <div
            className={`px-6 pb-6 ${
              activeSection === 'account' && adminOrganizations.length > 1 ? 'pt-3.5' : 'pt-6'
            }`}
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
